import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterOutlet, Router, RouterLink } from '@angular/router';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { UserInfo } from '@core/services/user-info';
import { AsyncPipe } from '@angular/common';
import { Ripple } from 'primeng/ripple';
import { StyleClass } from 'primeng/styleclass';
import { map, Observable, startWith } from 'rxjs';
import { Role } from '@auth/models/role';
import { RolePermission } from '@auth/directives/role-permission';
import { environment } from '@environment';
import version from '@version';
import { Breadcrumb } from '../breadcrumb/breadcrumb';

interface Version {
  releaseName: string;
  versionNumber: string;
}

interface Module {
  name: string;
  routerLink: string | null;
  icon: string | null;
  sections: Section[];
}

interface Section {
  name: string;
  icon: string;
  routerLink: string;
  requiredRoles: Role[];
}

@Component({
  selector: 'm4w-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    DrawerModule,
    ButtonModule,
    AvatarModule,
    MenuModule,
    Ripple,
    StyleClass,
    AsyncPipe,
    RolePermission,
    RouterLink,
    Breadcrumb,
  ],
  templateUrl: './layout.html',
  styleUrls: ['./layout.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Layout {
  private readonly userInfo = inject(UserInfo);
  private readonly router = inject(Router);

  protected readonly isTest = !environment.production;
  protected readonly version: Version = version;

  protected readonly modules: Module[] = [
    {
      name: 'Dashboard',
      routerLink: '/dashboard',
      icon: 'pi-home',
      sections: [],
    },
    {
      name: 'Teaching',
      routerLink: null,
      icon: 'pi-graduation-cap',
      sections: [
        {
          name: 'Teachers',
          icon: 'pi-users',
          routerLink: '/teaching/teachers',
          requiredRoles: [],
        },
        {
          name: 'Content Management',
          icon: 'pi-folder',
          routerLink: '/teaching/content-management',
          requiredRoles: [],
        },
        {
          name: 'Lessons',
          icon: 'pi-list',
          routerLink: '/teaching/lessons',
          requiredRoles: [],
        },
        {
          name: 'Schedules',
          icon: 'pi-calendar',
          routerLink: '/teaching/schedules',
          requiredRoles: [],
        },
      ],
    },
  ];

  protected readonly authenticated$ = this.userInfo.authenticated$;
  protected readonly menuItems$: Observable<MenuItem[]> = this.userInfo.user$.pipe(
    map((currentUser) => [
      {
        label: `${currentUser.userName} (${currentUser.city.name})`,
      },
      {
        separator: true,
      },
      {
        label: 'Log out',
        icon: 'pi pi-sign-out',
        command: () => this.logOut(),
      },
    ]),
    startWith([]),
  );
  protected sidebarVisible = signal(false);

  public toggleSidebar() {
    this.sidebarVisible.update((visible) => !visible);
  }

  private logOut() {
    this.userInfo.clear();
    void this.router.navigate(['/auth/login']);
  }
}
